import React, { memo, useCallback, useContext, useMemo, useState } from "react";
import { Box, Checkbox, Collapse, Tooltip, Typography } from "@mui/material";
import "./styles/AttachmentRow.scss";
import AppContext from "../../../context/appContext";
import VersionRow from "./VersionRow";
import ActivityBadge from "./ActivityBadge";
import { handleDramaturgyTypeByFormat } from "../../../shared/utils/handleDramaturgyTypeByFormat";
import { useAddendumAssociations } from "../../../hooks/useAddendumAssociations";

const isChapterOrEpisode = (type) =>
  ["Capítulo/Episódio", "Episódio"].includes(type);

const getValidVersions = (versions) => versions.filter((item) => !!item.fileId);

const isChapterFormat = (format) =>
  handleDramaturgyTypeByFormat(format) === "Dramaturgia Longa";

const AttachmentRow = memo(
  ({ file, index, format, onToggleParent, onToggleVersion }) => {
    const { formatDate, convertDatePtBr } = useContext(AppContext);
    const [versionsOpen, setVersionsOpen] = useState(false);

    const { parent, version } = file;
    const typeName = parent.attachmentType?.name;
    const isChEp = isChapterOrEpisode(typeName);
    const isAdendo = typeName === "Adendo";
    const addendumId = parent.attachmentId;
    const isChapterAddendum = useMemo(() => isChapterFormat(format), [format]);

    const { addendumAssociations, isLoadingAddendumAssociations } =
      useAddendumAssociations({
        isAdendo,
        addendumId,
        isChapter: isChapterAddendum,
      });

    const validVersions = useMemo(() => getValidVersions(version), [version]);
    const fileHasVersions = validVersions.length > 0;

    const handleCheckBoxClick = useCallback(
      () => onToggleParent(file, index),
      [onToggleParent, file, index]
    );

    const handleToggleVersions = useCallback(() => {
      if (fileHasVersions) setVersionsOpen((prev) => !prev);
    }, [fileHasVersions]);

    return (
      <Box component="div" className="attachmentItem">
        <Box component="ul" className="mainVersionItem">
          <Box component="li" className="type" onClick={handleCheckBoxClick}>
            <Checkbox
              checked={!!parent.isActive}
              size="small"
              sx={{
                mr: "8px",
                color: "#c0c3f8",
                "&.Mui-checked": { color: "#17b4e1" },
              }}
            />
            <Box component="span" className="typeName">
              {typeName}
              {` ${isChEp ? parent.chapterNumber : ""}`}
            </Box>
            {isAdendo && isLoadingAddendumAssociations && (
              <Box component="span" className="addendumAssociationsSkeleton" />
            )}
            {isAdendo &&
              !isLoadingAddendumAssociations &&
              !!addendumAssociations && (
                <Box component="span" className="addendumAssociations">
                  {addendumAssociations}
                </Box>
              )}
            {isChEp && parent.blockNumber > 0 && (
              <Box component="span" className="blockNumber">
                Bloco {parent.blockNumber}
              </Box>
            )}
          </Box>

          <Box
            component="li"
            className={`version ${fileHasVersions ? "hasVersions" : ""}`}
            onClick={handleToggleVersions}
          >
            {typeName !== "Adendo" && (
              <>
                <Box component="span" className="versionNumber">
                  {!!version && parent.version}ª
                </Box>
                <Box component="span" className="versionText">
                  versão
                </Box>
              </>
            )}
            {fileHasVersions && (
              <div
                className="itemArrow"
                style={
                  versionsOpen ? { transform: "rotate(90deg)" } : undefined
                }
              />
            )}
          </Box>

          <Box component="li" className="file">
            {parent.fileName}
          </Box>

          <Box component="li" className="receivedAt">
            {formatDate(convertDatePtBr(parent.receivedAt))}
          </Box>

          <Box component="li" className="activitiesNumber">
            <Tooltip
              title={
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "Globotipo Text" }}
                >
                  Total de atividades criadas: {parent.activityCount ?? 0}
                </Typography>
              }
              arrow
              placement="top"
            >
              <Box component="span">
                <ActivityBadge activityCount={parent.activityCount} />
              </Box>
            </Tooltip>
          </Box>
        </Box>

        <Collapse in={versionsOpen} timeout={0} unmountOnExit>
          <Box component="ul" className="versionsList">
            {validVersions.map((item, i) => (
              <VersionRow
                key={i}
                item={item}
                index={index}
                versionIndex={i}
                onCheckBoxClick={onToggleVersion}
                formatDate={formatDate}
                convertDatePtBr={convertDatePtBr}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  }
);

export default AttachmentRow;
